/**
 * Schema Tab Component
 *
 * Displays database tables and columns discovered through research
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertCircle, Database, Search, Table as TableIcon, ChevronDown, ChevronUp, Key, Type } from 'lucide-react';
import { getSystemTables } from '../../lib/api/enterpriseSystemsApi';

interface SchemaTabProps {
  systemId: string;
}

const SchemaTab: React.FC<SchemaTabProps> = ({ systemId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [groupByModule, setGroupByModule] = useState(true);

  const { data: tables = [], isLoading, error } = useQuery({
    queryKey: ['system-tables', systemId],
    queryFn: () => getSystemTables(systemId),
  });

  const toggleTableExpansion = (tableId: string) => {
    setExpandedTables((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tableId)) {
        newSet.delete(tableId);
      } else {
        newSet.add(tableId);
      }
      return newSet;
    });
  };

  // Filter tables based on search
  const filteredTables = tables.filter((table: any) =>
    table.table_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    table.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    table.module_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group tables by module
  const tablesByModule = filteredTables.reduce((acc: any, table: any) => {
    const module = table.module_name || 'Other';
    if (!acc[module]) acc[module] = [];
    acc[module].push(table);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        <span className="ml-3 text-neutral-600">Loading schema...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Schema</h3>
            <p className="text-sm text-red-700 mt-1">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (tables.length === 0) {
    return (
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-12 text-center">
        <Database className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">No Tables Found</h3>
        <p className="text-neutral-600">
          Run AI research to discover database tables and schema
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-neutral-900">Database Schema</h2>
        <p className="text-sm text-neutral-600 mt-1">
          Discovered tables and columns ({tables.length} tables)
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search tables..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Group Toggle */}
      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
          <input
            type="checkbox"
            checked={groupByModule}
            onChange={(e) => setGroupByModule(e.target.checked)}
            className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          />
          Group by Module
        </label>
      </div>

      {/* Tables Display */}
      {groupByModule ? (
        <div className="space-y-6">
          {Object.entries(tablesByModule).map(([module, moduleTables]: [string, any]) => (
            <div key={module}>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">
                {module} ({moduleTables.length} tables)
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {moduleTables.map((table: any) => {
                  const isExpanded = expandedTables.has(table.table_id);
                  const columns = table.columns || [];
                  const hasColumns = columns.length > 0;

                  return (
                    <div
                      key={table.table_id}
                      className="bg-white border border-neutral-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
                          <TableIcon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base font-semibold text-neutral-900 break-words">
                            {table.table_name}
                          </h4>
                          {table.module_code && (
                            <p className="text-xs text-neutral-500 mt-0.5">
                              Code: {table.module_code}
                            </p>
                          )}
                        </div>
                        {(table.column_count || hasColumns) && (
                          <button
                            onClick={() => toggleTableExpansion(table.table_id)}
                            className="flex items-center gap-1 px-2.5 py-1 bg-neutral-100 text-neutral-700 text-xs font-medium rounded-full hover:bg-neutral-200 transition-colors flex-shrink-0"
                          >
                            {table.column_count || columns.length} cols
                            {isExpanded ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )}
                          </button>
                        )}
                      </div>

                      {table.description && (
                        <p className="text-sm text-neutral-600 mb-3">
                          {table.description}
                        </p>
                      )}

                      {table.business_purpose && (
                        <p className="text-sm text-neutral-600 mb-3">
                          <span className="font-medium">Purpose:</span> {table.business_purpose}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-3 text-xs text-neutral-500">
                        {table.primary_key && (
                          <div className="flex items-center gap-1">
                            <Key className="w-3 h-3" />
                            <span className="font-medium">PK:</span> {table.primary_key}
                          </div>
                        )}
                        {table.record_count !== undefined && (
                          <span>
                            <span className="font-medium">Records:</span> {table.record_count.toLocaleString()}
                          </span>
                        )}
                      </div>

                      {/* Expanded Columns Section */}
                      {isExpanded && hasColumns && (
                        <div className="mt-4 p-3 bg-neutral-50 rounded-md border border-neutral-200">
                          <h5 className="text-xs font-semibold text-neutral-700 mb-2 uppercase flex items-center gap-1">
                            <Type className="w-3 h-3" />
                            Columns
                          </h5>
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {columns.map((column: any, idx: number) => (
                              <div
                                key={idx}
                                className="bg-white p-2 rounded border border-neutral-200"
                              >
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <code className="text-xs font-mono text-neutral-900 font-semibold">
                                    {column.column_name || column.name || `column_${idx}`}
                                  </code>
                                  <div className="flex gap-1 flex-shrink-0">
                                    {column.data_type && (
                                      <span className="text-xs px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded font-mono">
                                        {column.data_type}
                                      </span>
                                    )}
                                    {column.is_primary_key && (
                                      <span className="text-xs px-1.5 py-0.5 bg-yellow-50 text-yellow-700 rounded">
                                        PK
                                      </span>
                                    )}
                                    {column.is_nullable === false && (
                                      <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-700 rounded">
                                        NOT NULL
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {column.description && (
                                  <p className="text-xs text-neutral-600 mt-1">
                                    {column.description}
                                  </p>
                                )}
                                {column.sample_values && column.sample_values.length > 0 && (
                                  <div className="text-xs text-neutral-500 mt-1">
                                    <span className="font-medium">Sample:</span>{' '}
                                    {column.sample_values.slice(0, 3).join(', ')}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredTables.map((table: any) => {
            const isExpanded = expandedTables.has(table.table_id);
            const columns = table.columns || [];
            const hasColumns = columns.length > 0;

            return (
              <div
                key={table.table_id}
                className="bg-white border border-neutral-200 rounded-lg p-5 hover:shadow-md transition-shadow"
              >
                {/* Same table card content as above */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
                    <TableIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base font-semibold text-neutral-900 break-words">
                      {table.table_name}
                    </h4>
                    {table.module_name && (
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Module: {table.module_name}
                      </p>
                    )}
                  </div>
                  {(table.column_count || hasColumns) && (
                    <button
                      onClick={() => toggleTableExpansion(table.table_id)}
                      className="flex items-center gap-1 px-2.5 py-1 bg-neutral-100 text-neutral-700 text-xs font-medium rounded-full hover:bg-neutral-200 transition-colors flex-shrink-0"
                    >
                      {table.column_count || columns.length} cols
                      {isExpanded ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      )}
                    </button>
                  )}
                </div>

                {table.description && (
                  <p className="text-sm text-neutral-600 mb-3">
                    {table.description}
                  </p>
                )}

                {table.business_purpose && (
                  <p className="text-sm text-neutral-600 mb-3">
                    <span className="font-medium">Purpose:</span> {table.business_purpose}
                  </p>
                )}

                <div className="flex flex-wrap gap-3 text-xs text-neutral-500">
                  {table.primary_key && (
                    <div className="flex items-center gap-1">
                      <Key className="w-3 h-3" />
                      <span className="font-medium">PK:</span> {table.primary_key}
                    </div>
                  )}
                  {table.record_count !== undefined && (
                    <span>
                      <span className="font-medium">Records:</span> {table.record_count.toLocaleString()}
                    </span>
                  )}
                </div>

                {isExpanded && hasColumns && (
                  <div className="mt-4 p-3 bg-neutral-50 rounded-md border border-neutral-200">
                    <h5 className="text-xs font-semibold text-neutral-700 mb-2 uppercase flex items-center gap-1">
                      <Type className="w-3 h-3" />
                      Columns
                    </h5>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {columns.map((column: any, idx: number) => (
                        <div
                          key={idx}
                          className="bg-white p-2 rounded border border-neutral-200"
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <code className="text-xs font-mono text-neutral-900 font-semibold">
                              {column.column_name || column.name || `column_${idx}`}
                            </code>
                            <div className="flex gap-1 flex-shrink-0">
                              {column.data_type && (
                                <span className="text-xs px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded font-mono">
                                  {column.data_type}
                                </span>
                              )}
                              {column.is_primary_key && (
                                <span className="text-xs px-1.5 py-0.5 bg-yellow-50 text-yellow-700 rounded">
                                  PK
                                </span>
                              )}
                              {column.is_nullable === false && (
                                <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-700 rounded">
                                  NOT NULL
                                </span>
                              )}
                            </div>
                          </div>
                          {column.description && (
                            <p className="text-xs text-neutral-600 mt-1">
                              {column.description}
                            </p>
                          )}
                          {column.sample_values && column.sample_values.length > 0 && (
                            <div className="text-xs text-neutral-500 mt-1">
                              <span className="font-medium">Sample:</span>{' '}
                              {column.sample_values.slice(0, 3).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {filteredTables.length === 0 && searchQuery && (
        <div className="text-center py-12 text-neutral-500">
          No tables match your search
        </div>
      )}
    </div>
  );
};

export default SchemaTab;
